import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import PublicLayout from '../components/PublicLayout';
import { api, useAuth } from '../App';
import { MapPin, Clock, IndianRupee, ArrowLeft, MessageSquare, ExternalLink, Briefcase, ArrowRight, Wrench, Globe, Linkedin, Building, Download, CheckCircle, Loader2 } from 'lucide-react';

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingCV, setDownloadingCV] = useState(false);
  
  const isClient = user?.role === 'client';
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get(`/profiles/${id}`);
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);
  
  const handleCVDownload = async () => {
    setDownloadingCV(true);
    try {
      const res = await api.get(`/profiles/${id}/cv`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(profile.full_name || profile.user?.name || 'researcher').replace(/\s+/g, '_')}_CV.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CV download error:', error);
      // Fallback to direct URL
      if (profile?.cv_url) window.open(profile.cv_url, '_blank');
    } finally {
      setDownloadingCV(false);
    }
  };

  const Layout = user ? DashboardLayout : PublicLayout;
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-800"></div>
        </div>
      </Layout>
    );
  }
  
  if (!profile) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-neutral-500">Profile not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-sm text-neutral-600 hover:underline">
            Go back
          </button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-6 transition-colors"
      >
        <ArrowLeft size={20} />
        Back
      </button>
      
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-neutral-200 p-8">
            {/* Header */}
            <div className="flex items-start gap-6 mb-8 pb-8 border-b border-neutral-200">
              {(profile.photo_url || profile.user?.picture) ? (
                <img 
                  src={profile.photo_url || profile.user.picture} 
                  alt={profile.full_name || profile.user?.name}
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#FFF0EB] flex items-center justify-center text-[#FF6B35] font-bold text-3xl">
                  {(profile.full_name || profile.user?.name)?.[0]?.toUpperCase()}
                </div>
              )}
              
              <div className="flex-1">
                <h1 
                  className="text-2xl lg:text-3xl font-bold mb-1"
                  style={{ fontFamily: 'Outfit' }}
                  data-testid="profile-name"
                >
                  {profile.full_name || profile.user?.name}
                </h1>
                {profile.headline && (
                  <p className="text-lg text-neutral-600 mb-3">{profile.headline}</p>
                )}
                
                <div className="flex flex-wrap gap-4 text-sm text-neutral-500">
                  {profile.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={16} />
                      {profile.location}
                    </span>
                  )}
                  {profile.experience_years && (
                    <span className="flex items-center gap-1">
                      <Clock size={16} />
                      {profile.experience_years} years experience
                    </span>
                  )}
                  {profile.hourly_rate && ['freelance', 'part_time', 'per_project'].includes(profile.arrangement_type) && (
                    <span className="flex items-center gap-1">
                      <IndianRupee size={16} />
                      ₹{profile.hourly_rate}/hr
                    </span>
                  )}
                  {profile.monthly_salary && profile.arrangement_type === 'full_time' && (
                    <span className="flex items-center gap-1">
                      <IndianRupee size={16} />
                      ₹{Number(profile.monthly_salary).toLocaleString('en-IN')}/mo
                    </span>
                  )}
                  {profile.stipend && profile.arrangement_type === 'internship' && (
                    <span className="flex items-center gap-1">
                      <IndianRupee size={16} />
                      ₹{Number(profile.stipend).toLocaleString('en-IN')}/mo stipend
                    </span>
                  )}
                </div>
                
                <span className={`inline-block mt-3 text-xs px-2 py-1 rounded-sm font-medium ${
                  profile.availability === 'available'
                    ? 'bg-green-100 text-green-700'
                    : profile.availability === 'busy'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-neutral-100 text-neutral-500'
                }`}>
                  {profile.availability?.replace('_', ' ') || 'Unknown'}
                </span>
              </div>
            </div>
            
            {/* Bio */}
            {profile.bio && (
              <div className="mb-8">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  About
                </h2>
                <p className="text-neutral-700 whitespace-pre-wrap" data-testid="profile-bio">
                  {profile.bio}
                </p>
              </div>
            )}
            
            {/* Skills */}
            {profile.skills?.length > 0 && (
              <div className="mb-8">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  Skills
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1.5 bg-[#FFF0EB] text-[#FF6B35] text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Methods */}
            {profile.methods?.length > 0 && (
              <div className="mb-8" data-testid="profile-methods">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  Research Methods
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.methods.map((method, i) => (
                    <span key={i} className="px-3 py-1.5 bg-orange-50 text-orange-700 text-sm font-medium border border-orange-200">
                      {method}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Sectors */}
            {profile.sectors?.length > 0 && (
              <div className="mb-8" data-testid="profile-sectors">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  Industry Sectors
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.sectors.map((sector, i) => (
                    <span key={i} className="px-3 py-1.5 bg-teal-50 text-teal-700 text-sm font-medium border border-teal-200">
                      {sector}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tools */}
            {profile.tools?.length > 0 && (
              <div className="mb-8" data-testid="profile-tools">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  <Wrench size={18} className="inline mr-2 text-neutral-400" />
                  Tools
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.tools.map((tool, i) => (
                    <span key={i} className="px-3 py-1.5 bg-neutral-100 text-neutral-700 text-sm font-medium">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Languages */}
            {profile.languages?.length > 0 && (
              <div className="mb-8" data-testid="profile-languages">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  <Globe size={18} className="inline mr-2 text-neutral-400" />
                  Languages
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((lang, i) => (
                    <span key={i} className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium border border-blue-200">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Certifications */}
            {profile.certifications?.length > 0 && (
              <div className="mb-8">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  Certifications
                </h2>
                <ul className="space-y-2">
                  {profile.certifications.map((cert, i) => (
                    <li key={i} className="flex items-center gap-2 text-neutral-700">
                      <Briefcase size={16} className="text-neutral-400" />
                      {cert}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Portfolio Links */}
            {profile.portfolio_links?.length > 0 && (
              <div className="mb-8">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  Portfolio
                </h2>
                <ul className="space-y-2">
                  {profile.portfolio_links.map((link, i) => (
                    <li key={i}>
                      <a 
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#FF6B35] hover:underline"
                      >
                        <ExternalLink size={16} />
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Company / Agency Details */}
            {(profile.company_name || profile.designation) && (
              <div className="mb-8" data-testid="profile-company">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  <Building size={18} className="inline mr-2 text-neutral-400" />
                  Company / Agency
                </h2>
                <div className="bg-neutral-50 border border-neutral-200 p-4 space-y-2">
                  {profile.company_name && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-500 text-sm w-28 shrink-0">Company</span>
                      <span className="font-medium text-neutral-800">{profile.company_name}</span>
                      {profile.company_email_verified && (
                        <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-sm">
                          <CheckCircle size={12} /> Verified
                        </span>
                      )}
                    </div>
                  )}
                  {profile.designation && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-500 text-sm w-28 shrink-0">Designation</span>
                      <span className="font-medium text-neutral-800">{profile.designation}</span>
                    </div>
                  )}
                  {profile.company_website && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-500 text-sm w-28 shrink-0">Website</span>
                      <a href={profile.company_website} target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:underline flex items-center gap-1">
                        <ExternalLink size={14} />{profile.company_website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Links */}
            {(profile.linkedin_url || profile.cv_url) && (
              <div data-testid="profile-links">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  Links & Documents
                </h2>
                <div className="flex flex-wrap gap-3">
                  {profile.linkedin_url && (
                    <a 
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white text-sm font-medium hover:bg-[#006097] transition-colors"
                    >
                      <Linkedin size={16} />
                      LinkedIn Profile
                    </a>
                  )}
                  {profile.cv_url && (
                    <button 
                      onClick={handleCVDownload}
                      disabled={downloadingCV}
                      className="flex items-center gap-2 px-4 py-2 bg-neutral-800 text-white text-sm font-medium hover:bg-neutral-700 transition-colors disabled:opacity-50"
                      data-testid="download-cv-btn"
                    >
                      {downloadingCV ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                      Download CV
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="lg:col-span-1">
          {/* Contact Card */}
          {isClient ? (
            <div className="bg-white border border-neutral-200 p-6 mb-6">
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>
                Contact Researcher
              </h3>
              <p className="text-sm text-neutral-500 mb-4">
                Interested in working with {profile.user?.name?.split(' ')[0]}? Send them a message.
              </p>
              <Link
                to={`/client/messages?to=${profile.user_id}`}
                data-testid="message-button"
                className="btn-client w-full py-3 flex items-center justify-center gap-2"
              >
                <MessageSquare size={18} />
                Send Message
              </Link>
            </div>
          ) : !user && (
            <div className="bg-white border border-neutral-200 p-6 mb-6" data-testid="public-cta-card">
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
                Interested in this researcher?
              </h3>
              <p className="text-sm text-neutral-500 mb-4">
                Sign up to message them, post briefs, or browse more profiles.
              </p>
              <button onClick={() => login('client')} data-testid="public-cta-join" className="btn-client w-full py-3 flex items-center justify-center gap-2 mb-2">
                Join as Client <ArrowRight size={16} />
              </button>
              <button onClick={() => login('researcher')} className="w-full py-2.5 text-sm text-[#FF6B35] border border-[#FF6B35] hover:bg-[#FFF0EB] flex items-center justify-center gap-2">
                Join as Researcher <ArrowRight size={16} />
              </button>
            </div>
          )}
          
          {/* Quick Stats */}
          <div className="bg-white border border-neutral-200 p-6">
            <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>
              Quick Info
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-neutral-500">Experience</span>
                <span className="font-medium">{profile.experience_years || 'N/A'} years</span>
              </div>
              {/* Adaptive compensation in sidebar */}
              {['freelance', 'part_time', 'per_project'].includes(profile.arrangement_type) && (
                <>
                  {profile.hourly_rate && (
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Hourly Rate</span>
                      <span className="font-medium">₹{Number(profile.hourly_rate).toLocaleString('en-IN')}/hr</span>
                    </div>
                  )}
                  {(profile.day_rate_min || profile.day_rate_max) && (
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Day Rate</span>
                      <span className="font-medium">
                        {profile.day_rate_min && profile.day_rate_max
                          ? `₹${Number(profile.day_rate_min).toLocaleString('en-IN')} - ₹${Number(profile.day_rate_max).toLocaleString('en-IN')}`
                          : profile.day_rate_min
                            ? `From ₹${Number(profile.day_rate_min).toLocaleString('en-IN')}`
                            : `Up to ₹${Number(profile.day_rate_max).toLocaleString('en-IN')}`}
                      </span>
                    </div>
                  )}
                </>
              )}
              {profile.arrangement_type === 'full_time' && profile.monthly_salary && (
                <div className="flex justify-between">
                  <span className="text-neutral-500">Monthly Salary</span>
                  <span className="font-medium">₹{Number(profile.monthly_salary).toLocaleString('en-IN')}</span>
                </div>
              )}
              {profile.arrangement_type === 'internship' && profile.stipend && (
                <div className="flex justify-between">
                  <span className="text-neutral-500">Monthly Stipend</span>
                  <span className="font-medium">₹{Number(profile.stipend).toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-neutral-500">Location</span>
                <span className="font-medium">{profile.location || 'N/A'}</span>
              </div>
              {profile.arrangement_type && (
                <div className="flex justify-between">
                  <span className="text-neutral-500">Arrangement</span>
                  <span className="font-medium capitalize">{profile.arrangement_type.replace('_', ' ')}</span>
                </div>
              )}
              {profile.researcher_type && (
                <div className="flex justify-between">
                  <span className="text-neutral-500">Type</span>
                  <span className="font-medium capitalize">{profile.researcher_type}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-neutral-500">Status</span>
                <span className={`font-medium ${
                  profile.availability === 'available' ? 'text-green-600' :
                  profile.availability === 'busy' ? 'text-yellow-600' : 'text-neutral-500'
                }`}>
                  {profile.availability?.replace('_', ' ') || 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
